import { diffLines } from "diff";

export interface DiffRow {
  type: "same" | "add" | "del";
  oldNo: number | null;
  newNo: number | null;
  text: string;
}

export interface DiffGap {
  type: "gap";
  count: number;
}

export type DiffLine = DiffRow | DiffGap;

export interface FileDiff {
  path: string;
  status: "added" | "removed" | "modified";
  rows: DiffLine[];
  added: number;
  removed: number;
}

/** Строит построчный дифф с номерами строк. */
function buildRows(oldText: string, newText: string): DiffRow[] {
  const parts = diffLines(oldText, newText);
  const rows: DiffRow[] = [];
  let oldNo = 1;
  let newNo = 1;

  for (const part of parts) {
    const lines = part.value.split("\n");
    if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
    for (const text of lines) {
      if (part.added) rows.push({ type: "add", oldNo: null, newNo: newNo++, text });
      else if (part.removed) rows.push({ type: "del", oldNo: oldNo++, newNo: null, text });
      else rows.push({ type: "same", oldNo: oldNo++, newNo: newNo++, text });
    }
  }
  return rows;
}

/** Оставляет контекст вокруг изменений, схлопывая длинные участки. */
function withContext(rows: DiffRow[], context = 3): DiffLine[] {
  const keep = new Array<boolean>(rows.length).fill(false);
  rows.forEach((row, i) => {
    if (row.type === "same") return;
    for (let j = Math.max(0, i - context); j <= Math.min(rows.length - 1, i + context); j++) {
      keep[j] = true;
    }
  });

  const out: DiffLine[] = [];
  let skipped = 0;
  rows.forEach((row, i) => {
    if (keep[i]) {
      if (skipped > 0) {
        out.push({ type: "gap", count: skipped });
        skipped = 0;
      }
      out.push(row);
    } else {
      skipped += 1;
    }
  });
  if (skipped > 0) out.push({ type: "gap", count: skipped });
  return out;
}

export interface DiffInput {
  path: string;
  content: string;
}

/** Сравнивает два набора файлов и возвращает только изменения. */
export function diffTrees(before: DiffInput[], after: DiffInput[]): FileDiff[] {
  const beforeMap = new Map(before.map((f) => [f.path, f.content]));
  const afterMap = new Map(after.map((f) => [f.path, f.content]));

  // Служебные файлы (.vetchka) показываем последними: дифф — про код.
  const paths = [...new Set([...beforeMap.keys(), ...afterMap.keys()])].sort((a, b) => {
    const ad = a.startsWith(".") ? 1 : 0;
    const bd = b.startsWith(".") ? 1 : 0;
    if (ad !== bd) return ad - bd;
    return a.localeCompare(b);
  });
  const result: FileDiff[] = [];

  for (const path of paths) {
    const oldText = beforeMap.get(path);
    const newText = afterMap.get(path);
    if (oldText === newText) continue;

    const status: FileDiff["status"] =
      oldText === undefined ? "added" : newText === undefined ? "removed" : "modified";

    const rows = buildRows(oldText ?? "", newText ?? "");
    const added = rows.filter((r) => r.type === "add").length;
    const removed = rows.filter((r) => r.type === "del").length;

    result.push({
      path,
      status,
      rows: withContext(rows),
      added,
      removed,
    });
  }

  return result;
}
